<template>
  <div class="page-report rb-page">
    <div class="page-header align-items-start min-vh-100">
      <span class="mask bg-white"></span>
      <div class="container">
        <div class="d-flex align-items-center gap-2 mt-4 mb-3 flex-wrap">
          <button type="button" class="btn btn-link mb-0 px-0" @click="leave">← {{ $t("rb_back") }}</button>
          <input
            id="rbName"
            v-model="name"
            class="form-control rb-name"
            :placeholder="$t('rb_name_placeholder')"
            :aria-label="$t('rb_name_placeholder')"
            maxlength="150"
            @input="dirty = true"
          />
          <material-button
            color="info"
            size="sm"
            class="mb-0"
            :disabled="!canSave"
            :title="saveHint"
            @click="save"
          >{{ $t("rb_save") }}</material-button>
        </div>

        <div v-if="meta" class="row g-3">
          <!-- Building the report -->
          <div class="col-xl-5 col-lg-6 col-12">
            <div class="rb-panel">
              <section class="rb-step">
                <div class="rb-hint mb-0">{{ $t("rb_journal_hint") }}</div>
              </section>

              <section class="rb-step">
                <h6>
                  <span class="rb-n">1</span>{{ $t("rb_step_columns") }}
                  <span class="rb-count">{{ def.columns.length }}/{{ meta.max_columns }}</span>
                </h6>
                <draggable
                  v-model="def.columns"
                  :item-key="colKey"
                  handle=".rb-drag"
                  ghost-class="rb-ghost"
                  :animation="150"
                  @end="onColumnMoved"
                >
                  <template #item="{ element: col, index: i }">
                <div :class="['rb-col', { err: errorAt('column', i) }]">
                  <span class="rb-drag material-icons-round" :title="$t('rb_drag')">drag_indicator</span>
                  <div class="rb-col-label">
                    <span class="rb-col-no">{{ i + 1 }}</span>
                    <div class="rb-col-name">
                      {{ colName(col) }}
                      <small>{{ fields[col.field]?.label || col.field }}</small>
                    </div>
                  </div>
                  <button type="button" class="rb-icon" :aria-label="$t('rb_remove')" @click="removeColumn(i)">✕</button>
                  <div class="rb-col-extra">
                    <div class="rb-ctl rb-ctl-title">
                      <label :for="`rbTitle${i}`">{{ $t("rb_col_title") }}</label>
                      <input
                        :id="`rbTitle${i}`"
                        class="form-control form-control-sm"
                        :value="col.title || ''"
                        :maxlength="meta.max_title"
                        :placeholder="fields[col.field]?.label || col.field"
                        @input="setColumn(i, 'title', $event.target.value)"
                      />
                    </div>
                    <div v-if="formatsFor(col).length" class="rb-ctl">
                      <label :for="`rbFormat${i}`">{{ $t("rb_col_format") }}</label>
                      <select
                        :id="`rbFormat${i}`"
                        class="form-select form-select-sm"
                        :value="col.format || ''"
                        @change="setColumn(i, 'format', $event.target.value)"
                      >
                        <option value="">{{ $t("rb_format_default") }}</option>
                        <option v-for="f in formatsFor(col)" :key="f.key" :value="f.key">{{ f.label }}</option>
                      </select>
                    </div>
                    <div class="rb-ctl">
                      <label :for="`rbAgg${i}`">{{ $t("rb_summary") }}</label>
                      <select
                        :id="`rbAgg${i}`"
                        class="form-select form-select-sm"
                        :value="col.agg || ''"
                        @change="setAgg(i, $event.target.value)"
                      >
                        <option value="">{{ $t("rb_agg_none") }}</option>
                        <option v-for="a in aggsFor(col.field)" :key="a" :value="a">{{ $t(`rb_agg_${a}`) }}</option>
                      </select>
                    </div>
                  </div>
                </div>
                  </template>
                </draggable>
                <p v-if="!def.columns.length" class="rb-hint">{{ $t("rb_no_columns") }}</p>
                <button
                  type="button"
                  class="btn btn-outline-info btn-sm mb-0"
                  :disabled="def.columns.length >= meta.max_columns"
                  @click="openPicker('column')"
                >+ {{ $t("rb_add_column") }}</button>
              </section>

              <section class="rb-step">
                <h6>
                  <span class="rb-n">2</span>{{ $t("rb_step_filters") }}
                  <span class="rb-count">{{ def.filters.length }}/{{ meta.max_filters }}</span>
                </h6>
                <div class="rb-chips">
                  <span v-for="(f, i) in def.filters" :key="i" :class="['rb-chip', { err: errorAt('filter', i) }]">
                    <button type="button" class="rb-chip-text" @click="editFilter(i)">
                      {{ fields[f.field]?.label || f.field }} {{ opWord(f.field, f.op) }}
                      <b v-if="f.op !== 'empty' && f.op !== 'notempty'">{{ f.value }}</b>
                    </button>
                    <button type="button" class="rb-icon" :aria-label="$t('rb_remove')" @click="removeFilter(i)">✕</button>
                  </span>
                  <button
                    type="button"
                    class="btn btn-outline-info btn-sm mb-0"
                    :disabled="def.filters.length >= meta.max_filters"
                    @click="openPicker('filter')"
                  >+ {{ $t("rb_add_filter") }}</button>
                </div>
              </section>

              <section class="rb-step">
                <h6><span class="rb-n">3</span>{{ $t("rb_step_sort") }}</h6>
                <div class="d-flex gap-2 flex-wrap">
                  <select id="rbSortCol" class="form-select form-select-sm rb-sort" :value="sortColumn" @change="setSort(Number($event.target.value), sortDesc)">
                    <option :value="-1">{{ $t("rb_sort_none") }}</option>
                    <option v-for="(col, i) in def.columns" :key="i" :value="i">{{ colName(col) }}</option>
                  </select>
                  <select
                    id="rbSortDir"
                    class="form-select form-select-sm rb-sort"
                    :value="sortDesc ? 1 : 0"
                    :disabled="sortColumn < 0"
                    @change="setSort(sortColumn, $event.target.value === '1')"
                  >
                    <option :value="0">{{ $t("rb_sort_asc") }}</option>
                    <option :value="1">{{ $t("rb_sort_desc") }}</option>
                  </select>
                </div>
                <p class="rb-hint">{{ $t("rb_sort_hint") }}</p>
              </section>

              <section class="rb-step">
                <h6><span class="rb-n">4</span>{{ $t("rb_step_export") }}</h6>
                <label class="d-flex align-items-center gap-2 text-sm mb-0" for="rbHeader">
                  <input id="rbHeader" v-model="exportHeader" type="checkbox" @change="dirty = true" />
                  {{ $t("rb_export_header") }}
                </label>
              </section>

              <section v-if="errors.length" class="rb-step">
                <div class="alert alert-danger text-white text-sm mb-0" role="alert">
                  <div v-for="(e, i) in errors" :key="i">{{ e.message }}</div>
                </div>
              </section>
            </div>
          </div>

          <!-- Live preview -->
          <div class="col-xl-7 col-lg-6 col-12">
            <div class="rb-panel">
              <div class="rb-preview-head">
                <h6 class="mb-0">{{ $t("rb_preview") }}</h6>
                <select
                  id="rbPreviewPeriod"
                  v-model="previewPreset"
                  class="form-select form-select-sm rb-preview-period"
                  :aria-label="$t('rb_period')"
                  @change="runPreview"
                >
                  <option v-for="p in presets" :key="p" :value="p">{{ $t(`rb_preset_${p}`) }}</option>
                </select>
                <span class="text-xs text-secondary">{{ $t("rb_preview_scope") }}</span>
                <span v-if="previewLoading" class="spinner-border spinner-border-sm text-info ms-auto" role="status"></span>
                <button v-else type="button" class="btn btn-link btn-sm mb-0 ms-auto" @click="runPreview">{{ $t("rb_test_run") }}</button>
              </div>
              <div class="p-3" aria-live="polite">
                <div v-if="previewMessage" class="rb-empty">{{ previewMessage }}</div>
                <report-result-table
                  v-else
                  :result="preview"
                  :sort="def.sort[0] || null"
                  sortable
                  @sort="sortByHeader"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Field picker -->
    <div v-if="picker" class="rb-scrim" @click.self="picker = null">
      <div class="rb-dialog">
        <div class="d-flex align-items-center mb-2">
          <h5 class="mb-0">{{ picker === "column" ? $t("rb_add_column") : $t("rb_filter_by") }}</h5>
          <button type="button" class="rb-icon ms-auto" :aria-label="$t('rb_close')" @click="picker = null">✕</button>
        </div>
        <input id="rbFieldSearch" ref="search" v-model="pickerQuery" class="form-control mb-2" :placeholder="$t('rb_search_fields')" />
        <div class="rb-field-list">
          <div v-for="(group, name) in pickerGroups" :key="name" class="mb-2">
            <div class="text-uppercase text-xs text-secondary fw-bold">{{ name }}</div>
            <button
              v-for="f in group"
              :key="f.key"
              type="button"
              class="rb-field"
              @click="pick(f)"
            >
              <span><b>{{ f.label }}</b><span v-if="picker === 'column' && usedFields.has(f.key)" class="text-info"> ✓</span></span>
              <small>{{ f.desc }}</small>
            </button>
          </div>
          <p v-if="!Object.keys(pickerGroups).length" class="rb-hint">{{ $t("rb_no_field_match", { q: pickerQuery }) }}</p>
        </div>
      </div>
    </div>

    <!-- Filter dialog -->
    <div v-if="filterDraft" class="rb-scrim" @click.self="filterDraft = null">
      <div class="rb-dialog">
        <div class="d-flex align-items-center mb-3">
          <h5 class="mb-0">{{ $t("rb_filter_title") }}</h5>
          <button type="button" class="rb-icon ms-auto" :aria-label="$t('rb_close')" @click="filterDraft = null">✕</button>
        </div>
        <div class="row g-2">
          <div class="col-6">
            <label class="text-sm" for="rbOp">{{ fields[filterDraft.field].label }}</label>
            <select id="rbOp" v-model="filterDraft.op" class="form-select">
              <option v-for="op in meta.operators[fields[filterDraft.field].type]" :key="op" :value="op">{{ opWord(filterDraft.field, op) }}</option>
            </select>
          </div>
          <div v-if="filterDraft.op !== 'empty' && filterDraft.op !== 'notempty'" class="col-6">
            <label class="text-sm" for="rbVal">{{ $t("rb_value") }}</label>
            <input
              v-if="filterType === 'date'"
              id="rbVal"
              v-model="filterDraft.value"
              class="form-control"
              :type="filterWithTime ? 'datetime-local' : 'date'"
            />
            <input
              v-else
              id="rbVal"
              v-model="filterDraft.value"
              class="form-control"
              :inputmode="filterType === 'number' ? 'decimal' : 'text'"
              :placeholder="filterType === 'number' ? '10.50' : ''"
            />
          </div>
        </div>
        <label v-if="filterType === 'date'" class="d-flex gap-2 align-items-center text-sm mt-2" for="rbWithTime">
          <input id="rbWithTime" v-model="filterWithTime" type="checkbox" @change="toggleFilterTime" />
          {{ $t("rb_compare_time") }}
        </label>
        <p v-if="filterType === 'text'" class="rb-hint mt-2">{{ $t("rb_text_hint") }}</p>
        <div v-if="filterError" class="alert alert-danger text-white text-sm py-2 mt-2 mb-0">{{ filterError }}</div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <material-button variant="outline" color="secondary" size="sm" @click="filterDraft = null">{{ $t("rb_cancel") }}</material-button>
          <material-button color="info" size="sm" @click="saveFilter">{{ filterIndex === null ? $t("rb_add_filter") : $t("rb_update_filter") }}</material-button>
        </div>
      </div>
    </div>

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="() => (snackbar = null)"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import "./reportBuilder.css";
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import AnalyticsService from "../../../services/analytics.service";
import ReportResultTable from "./ReportResultTable.vue";
import { PRESETS, columnName, emptyDefinition, fieldMap, presetRange, toInput } from "./reportBuilder";

const OP_WORDS = {
  number: { eq: "=", ne: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤" },
  date: { eq: "rb_op_on", gt: "rb_op_after", gte: "rb_op_on_after", lt: "rb_op_before", lte: "rb_op_on_before" },
  text: { eq: "rb_op_is", ne: "rb_op_is_not", contains: "rb_op_contains", empty: "rb_op_empty", notempty: "rb_op_notempty" },
};

export default {
  name: "ReportBuilderEditor",
  components: { draggable, MaterialButton, MaterialSnackbar, MaterialLoading, ReportResultTable },
  data() {
    return {
      meta: null,
      id: null,
      name: "",
      exportHeader: true,
      def: emptyDefinition(),
      dirty: false,
      serverErrors: [],
      preview: null,
      previewMessage: "",
      previewLoading: false,
      colKeys: new WeakMap(),
      colKeySeq: 0,
      presets: PRESETS,
      previewPreset: "yesterday",
      previewTimer: null,
      previewAbort: null,
      picker: null,
      pickerQuery: "",
      filterDraft: null,
      filterIndex: null,
      filterWithTime: false,
      filterError: "",
      loaderActive: false,
      snackbar: null,
      message: "",
    };
  },
  computed: {
    fields() {
      return fieldMap(this.meta);
    },
    usedFields() {
      return new Set(this.def.columns.map((c) => c.field));
    },
    // Checked here as the server checks it, so a problem shows the moment it is made.
    errors() {
      if (!this.meta) return [];
      const errs = [];
      const src = this.meta.sources[this.def.source];
      if (!this.def.columns.length) errs.push({ message: this.$t("rb_err_columns") });
      this.def.columns.forEach((c, i) => {
        const f = this.fields[c.field];
        if (!f || !f.sources.includes(this.def.source)) errs.push({ column: i, message: `"${f ? f.label : c.field}" isn't available in ${src.label}.` });
        else if (c.agg && !this.meta.aggregates[f.type].includes(c.agg)) errs.push({ column: i, message: `"${f.label}" can't be shown as ${c.agg}.` });
      });
      this.def.filters.forEach((x, i) => {
        const f = this.fields[x.field];
        if (!f || !f.sources.includes(this.def.source)) errs.push({ filter: i, message: `"${f ? f.label : x.field}" isn't available in ${src.label}.` });
      });
      return errs.concat(this.serverErrors);
    },
    canSave() {
      return !!this.name.trim() && !this.errors.length;
    },
    saveHint() {
      if (!this.name.trim()) return this.$t("rb_need_name");
      return this.errors.length ? this.$t("rb_fix_first") : "";
    },
    sortColumn() {
      return this.def.sort.length ? this.def.sort[0].column : -1;
    },
    sortDesc() {
      return this.def.sort.length ? !!this.def.sort[0].desc : true;
    },
    pickerGroups() {
      const q = this.pickerQuery.trim().toLowerCase();
      const groups = {};
      (this.meta?.fields || [])
        .filter((f) => f.sources.includes(this.def.source))
        .filter((f) => !q || f.label.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q))
        .forEach((f) => (groups[f.group] = groups[f.group] || []).push(f));
      return groups;
    },
    filterType() {
      return this.filterDraft ? this.fields[this.filterDraft.field].type : null;
    },
  },
  watch: {
    def: {
      deep: true,
      handler() {
        this.serverErrors = [];
        this.schedulePreview();
      },
    },
  },
  async created() {
    this.loaderActive = true;
    try {
      const fields = await AnalyticsService.reportBuilderFields();
      this.meta = fields.data.data;
      const id = this.$route.params.id;
      if (id) {
        const res = await AnalyticsService.reportBuilderDetail(id);
        if (!res.data.result.isSuccess) {
          this.notify(res.data.result.message, "danger");
          return;
        }
        const report = res.data.data.report;
        this.id = report.id;
        this.name = report.name;
        this.exportHeader = !!report.export_header;
        this.def = report.definition;
      }
      this.$nextTick(() => (this.dirty = false));
    } catch (e) {
      this.notify(this.$t("rb_load_failed"), "danger");
    } finally {
      this.loaderActive = false;
    }
  },
  beforeUnmount() {
    clearTimeout(this.previewTimer);
    if (this.previewAbort) this.previewAbort.abort();
  },
  methods: {
    colName(col) {
      return columnName(this.meta, col);
    },
    aggsFor(key) {
      const f = this.fields[key];
      return f ? this.meta.aggregates[f.type] : [];
    },
    opWord(key, op) {
      const f = this.fields[key];
      const word = f ? OP_WORDS[f.type][op] : op;
      return word && word.startsWith("rb_") ? this.$t(word) : word;
    },
    errorAt(kind, index) {
      return this.errors.some((e) => e[kind] === index);
    },
    // Changed in place, so the column keeps its drag key and the input being typed in keeps focus.
    setAgg(i, agg) {
      this.setColumn(i, "agg", agg);
    },
    // Column title, format or summary; an empty one goes back to the default.
    setColumn(i, key, value) {
      const col = this.def.columns[i];
      if (value.trim()) col[key] = value;
      else delete col[key];
      this.dirty = true;
    },
    // A stable key per column object for the drag list (a column has no id of its own).
    colKey(col) {
      if (!this.colKeys.has(col)) this.colKeys.set(col, ++this.colKeySeq);
      return this.colKeys.get(col);
    },
    formatsFor(col) {
      const type = this.fields[col.field]?.type;
      if (col.agg === "count" || col.agg === "count_distinct") return [];
      return (this.meta.formats || {})[type] || [];
    },
    // The sort points at a column by position: follow the column that was dragged.
    onColumnMoved({ oldIndex, newIndex }) {
      if (oldIndex === newIndex) return;
      const moved = (c) => {
        if (c === oldIndex) return newIndex;
        if (oldIndex < newIndex && c > oldIndex && c <= newIndex) return c - 1;
        if (oldIndex > newIndex && c >= newIndex && c < oldIndex) return c + 1;
        return c;
      };
      this.def.sort = this.def.sort.map((s) => ({ ...s, column: moved(s.column) }));
      this.dirty = true;
    },
    removeColumn(i) {
      this.def.columns.splice(i, 1);
      this.def.sort = this.def.sort
        .filter((s) => s.column !== i)
        .map((s) => ({ ...s, column: s.column > i ? s.column - 1 : s.column }));
      this.dirty = true;
    },
    setSort(column, desc) {
      this.def.sort = column < 0 ? [] : [{ column, desc }];
      this.dirty = true;
    },
    sortByHeader(i) {
      const current = this.def.sort[0];
      this.setSort(i, current && current.column === i ? !current.desc : true);
    },
    openPicker(kind) {
      this.picker = kind;
      this.pickerQuery = "";
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },
    pick(field) {
      const kind = this.picker;
      this.picker = null;
      if (kind === "column") {
        this.def.columns.push({ field: field.key });
        this.dirty = true;
      } else {
        const op = this.meta.operators[field.type][0];
        this.openFilter({ field: field.key, op, value: "" }, null);
      }
    },
    editFilter(i) {
      this.openFilter({ ...this.def.filters[i] }, i);
    },
    openFilter(draft, index) {
      this.filterDraft = draft;
      this.filterIndex = index;
      this.filterWithTime = String(draft.value).includes("T");
      this.filterError = "";
    },
    toggleFilterTime() {
      const v = this.filterDraft.value || "";
      this.filterDraft.value = this.filterWithTime ? (v ? `${v.slice(0, 10)}T00:00` : "") : v.slice(0, 10);
    },
    saveFilter() {
      const f = this.fields[this.filterDraft.field];
      const draft = { ...this.filterDraft, value: String(this.filterDraft.value ?? "").trim() };
      const needsValue = draft.op !== "empty" && draft.op !== "notempty";
      if (needsValue && f.type === "number" && !/^-?\d+(\.\d+)?$/.test(draft.value)) {
        this.filterError = `"${f.label}" needs a number to compare with.`;
        return;
      }
      if (needsValue && draft.value === "") {
        this.filterError = `Enter a value for the "${f.label}" filter.`;
        return;
      }
      if (!needsValue) draft.value = "";
      if (this.filterIndex === null) this.def.filters.push(draft);
      else this.def.filters.splice(this.filterIndex, 1, draft);
      this.filterDraft = null;
      this.dirty = true;
    },
    removeFilter(i) {
      this.def.filters.splice(i, 1);
      this.dirty = true;
    },
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(this.runPreview, 800);
    },
    async runPreview() {
      clearTimeout(this.previewTimer);
      if (!this.meta) return;
      if (this.errors.length) {
        this.preview = null;
        this.previewMessage = this.def.columns.length ? this.$t("rb_preview_fix") : this.$t("rb_preview_empty");
        return;
      }
      // A newer change cancels the run still in flight.
      if (this.previewAbort) this.previewAbort.abort();
      const abort = new AbortController();
      this.previewAbort = abort;
      this.previewLoading = true;
      const [from, to] = presetRange(this.previewPreset);
      try {
        const res = await AnalyticsService.reportBuilderRun(
          {
            definition: this.def,
            mode: "test",
            branch_id: -1,
            physical_branch_id: -1,
            from_date: toInput(from),
            to_date: toInput(to),
          },
          { signal: abort.signal }
        );
        if (res.data.result.isSuccess) {
          this.preview = res.data.data;
          this.previewMessage = "";
        } else {
          this.preview = null;
          this.serverErrors = res.data.data?.errors || [];
          this.previewMessage = res.data.result.message;
        }
      } catch (e) {
        if (abort.signal.aborted) return;
        this.preview = null;
        this.previewMessage = this.$t("rb_run_failed");
      } finally {
        if (this.previewAbort === abort) this.previewLoading = false;
      }
    },
    async save() {
      if (!this.canSave) return;
      this.loaderActive = true;
      try {
        const res = await AnalyticsService.reportBuilderSave({
          id: this.id,
          name: this.name.trim(),
          definition: this.def,
          export_header: this.exportHeader ? 1 : 0,
        });
        if (res.data.result.isSuccess) {
          this.dirty = false;
          this.$router.push({ name: "ReportBuilderList" });
        } else {
          this.serverErrors = res.data.data?.errors || [];
          this.notify(res.data.result.message, "danger");
        }
      } catch (e) {
        this.notify(this.$t("rb_save_failed"), "danger");
      } finally {
        this.loaderActive = false;
      }
    },
    leave() {
      this.$router.push({ name: "ReportBuilderList" });
    },
    notify(message, type) {
      this.message = message;
      this.snackbar = type;
      setTimeout(() => (this.snackbar = null), 3000);
    },
  },
  beforeRouteLeave(to, from, next) {
    // Same unsaved-changes guard as PromoCodeDetail.
    if (this.dirty && !window.confirm(this.$t("rb_unsaved"))) {
      next(false);
      return;
    }
    next();
  },
};
</script>

<style scoped>
.rb-name {
  flex: 1;
  min-width: 200px;
  max-width: 520px;
  font-size: 1.1rem;
  font-weight: 600;
  border: 1px solid #dee2e6 !important;
  padding: 6px 10px !important;
}
.rb-panel {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
.rb-step {
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f0;
}
.rb-step:last-child {
  border-bottom: 0;
}
.rb-step h6 {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.rb-n {
  color: #0d7a6c;
  font-size: 0.75rem;
}
.rb-count {
  margin-left: auto;
  font-weight: 400;
  font-size: 0.75rem;
  color: #6c757d;
}
.rb-col {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 6px 8px;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 8px 8px 10px 6px;
  margin-bottom: 8px;
  background: #fff;
}
/* Title, format and summary under the column name, each with its own small label. */
.rb-col-extra {
  grid-column: 2 / -1;
  display: grid;
  /* The title takes the room left; format and summary are as wide as their longest option. */
  grid-template-columns: minmax(90px, 1fr) auto auto;
  gap: 6px;
}
.rb-ctl {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rb-ctl label {
  font-size: 0.68rem;
  color: #6c757d;
  margin: 0;
}
.rb-ctl .form-control,
.rb-ctl .form-select {
  font-size: 0.8rem;
}
.rb-ctl .form-select {
  padding-left: 0.5rem;
  padding-right: 1.5rem;
  background-position: right 0.35rem center;
}
.rb-col.err,
.rb-chip.err {
  border-color: #dc3545;
}
.rb-col-label {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.rb-col-no {
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #eef8f6;
  color: #0d7a6c;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rb-col-name {
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rb-col-name small {
  display: block;
  font-weight: 400;
  color: #6c757d;
  font-size: 0.7rem;
}
.rb-drag {
  color: #adb5bd;
  font-size: 1.25rem;
  cursor: grab;
  user-select: none;
}
.rb-drag:hover {
  color: #0d7a6c;
}
.rb-ghost {
  opacity: 0.5;
  background: #eef8f6;
  border-style: dashed;
}
.rb-sort {
  width: auto;
  max-width: 100%;
  font-size: 0.8rem;
}
.rb-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.rb-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dee2e6;
  background: #f5f7f7;
  border-radius: 99px;
  padding: 2px 4px 2px 10px;
  font-size: 0.8rem;
  max-width: 100%;
}
.rb-chip-text {
  border: 0;
  background: none;
  padding: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rb-icon {
  border: 0;
  background: none;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1;
}
.rb-icon:hover:not(:disabled) {
  background: #f0f2f2;
}
.rb-hint {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 6px 0 0;
}
.rb-preview-period {
  width: auto;
}
.rb-preview-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid #eef0f0;
}
.rb-empty {
  padding: 32px 12px;
  text-align: center;
  color: #6c757d;
}
.rb-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px;
  overflow: auto;
}
.rb-dialog {
  background: #fff;
  border-radius: 10px;
  padding: 18px;
  width: min(520px, 100%);
}
.rb-field-list {
  max-height: 55vh;
  overflow: auto;
}
.rb-field {
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
  border: 0;
  background: none;
  padding: 6px 8px;
  border-radius: 6px;
}
.rb-field:hover {
  background: #f0f5f4;
}
.rb-field small {
  color: #6c757d;
}
</style>
